import React from 'react'
import i18next from 'i18next'
import bowser from 'bowser'

/**
 * A component that contains a abstraction for handling translations and
 * will add the property "_isMounted" which can be checked for to ensure
 * that the component still is mounted.
 */
export default class Component extends React.Component {
  constructor(props, context) {
    super(props, context)

    const self = this
    this.setThisState = function setThisState() {
      if (self._isMounted) {
        self.setState(this)
      }
    }
  }

  /**
   * When the component mounts, it will check for autoForceUpdate. If it exists, we add
   * an event listener to the window 'resize' event.
   * It also accepts an onAutoForceUpdate callback.
   * @param {*} props
   */
  componentDidMount(props) {
    this._isMounted = true

    const { autoForceUpdate, onAutoForceUpdate = null } = props || {}
    if (autoForceUpdate) {
      this.enableAutoForceUpdate(onAutoForceUpdate)
    }
  }

  componentWillUnmount() {
    this._isMounted = false

    if (this.willAutoForceUpdate) {
      window.removeEventListener('resize', this.doForceUpdate)
    }
  }

  /** Forwards the provided properties to "i18next.t" and returns the values  */
  t(...args) {
    return i18next.t(...args)
  }

  /**
   * Convenience function that will check if a certain "prop" has changed.
   * @param {*} prop - The name of the property that will be checked
   * @param {*} nextProps - The "nextProps" from the "componentWillReceiveProps"
   */
  propUpdated = (prop, nextProps) => {
    return nextProps[prop] !== this.props[prop]
  }

  /**
   * Convenience function that will check if a certain "prop" exists and has changed.
   * @param {*} prop - The name of the property that will be checked
   * @param {*} nextProps - The "nextProps" from the "componentWillReceiveProps"
   */
  propChanged = (prop, nextProps) => {
    return nextProps[prop] && this.propUpdated(prop, nextProps)
  }

  /**
   * Enables automatic calling of this.forceUpdate on resize of this component.
   * Will accept onAutoForceUpdate which will be called in conjunction with the forceUpdate.
   */
  enableAutoForceUpdate = (onAutoForceUpdate) => {
    if (this.willAutoForceUpdate) {
      return
    }
    this.willAutoForceUpdate = true
    this.onAutoForceUpdate = onAutoForceUpdate
    window.addEventListener('resize', this.doForceUpdate)
  }

  /**
   * Function called on the window 'resize' event.
   * Automatically adds a short timeout when using iOS Chrome, due to incorrect width / height values
   * if updated too soon.
   */
  doForceUpdate = () => {
    if (bowser.ios && !bowser.safari) {
      if (this.autoForceUpdateTimeout) {
        clearTimeout(this.autoForceUpdateTimeout)
        delete this.autoForceUpdateTimeout
      }

      this.autoForceUpdateTimeout = setTimeout(() => {
        this.onAutoForceUpdate && this.onAutoForceUpdate()
        this.forceUpdate()
      }, 300)
    } else {
      this.onAutoForceUpdate && this.onAutoForceUpdate()
      this.forceUpdate()
    }
  }
}
